import {
  CompanyShareableType,
  ModelError_Item,
  ModelError_Response,
} from '@treadinc/horizon-api-spec';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { Account } from '~hooks/useAccount/models';
import useAccount from '~hooks/useAccount/useAccount';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { DriverBasic } from '~hooks/useDrivers/models';
import { BasicEquipment } from '~hooks/useEquipment/models';
import useReportSaving from '~hooks/useReportSaving/useReportSaving';
import { ConnectedAccountForm as AccountForm } from '~pages/Settings/Accounts/ConnectedAccountForm';
import { useStores } from '~store/RootStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { AccountFormStateChangeProps } from './AccountFormStateChangeProps';
import CustomerAccountForm from './CustomerAccountForm/CustomerAccountForm';
import { useCompanySharesDetails } from './useCompanyShareDetails';
import VendorAccountFormDialogWrapper from './VendorAccountForm/VendorAccountFormDialogWrapper';

export type AccountSaveChange = 'created' | 'updated';

interface AccountsDialogProps {
  defaultAccount?: Account;
  onClose?: () => void;
  onSavingChange?: (isSaving: boolean) => void;
  onSuccess?: (change: AccountSaveChange) => void;
  open?: boolean;
  source?: 'customers-page' | 'vendors-page';
}

const AccountsDialog = observer(
  ({
    open,
    defaultAccount,
    onClose,
    onSavingChange,
    onSuccess,
    source,
  }: AccountsDialogProps) => {
    const selectedAccountId = defaultAccount?.id ?? null;
    const { toasterStore, userStore, companyAssetsStore } = useStores();

    const [currentFormDirty, setCurrentFormDirty] = useState<boolean>(false);
    const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
    const {
      drivers,
      equipment,
      sharedDriversCompanyShares,
      sharedEquipmentCompanyShares,
      setEquipment,
      setDrivers,
      setSharedDriversCompanyShares,
      setSharedEquipmentCompanyShares,
    } = useCompanySharesDetails({
      isAccountDialogOpen: open || false,
      selectedAccountId,
      selectedAccount: defaultAccount ?? null,
    });
    const accountActionsRef = useRef<any>(null);

    const {
      updateAccount,
      createAccount,
      createConnectedAccount,
      createConnectedAccountByCompanyId,
    } = useAccount();
    const [isLoading, setIsLoading] = useState(false);

    const [isFormSaving, setIsFormSaving] = useState(false);
    const isSaving = isLoading || isFormSaving;
    const { createCompanyShareByCompanyId, deleteCompanyShare } = useCompanyShares();
    useReportSaving(isSaving, onSavingChange);
    const onFormStateChange = ({ isDirty }: AccountFormStateChangeProps) => {
      setCurrentFormDirty(isDirty);
    };
    const companyId = getEffectiveUserCompanyId(userStore);

    const handleClose = () => {
      onClose?.();
      setEquipment([]);
      setDrivers([]);
      setSharedDriversCompanyShares([]);
      setSharedEquipmentCompanyShares([]);
      setAsyncErrors([]);
    };
    const handleSaveSuccess = (message: string, change: AccountSaveChange) => {
      toasterStore.push(alert(message, AlertTypes.success));
      onSuccess?.(change);
      handleClose();
    };
    const handleAccountCreated = (createdCompanyShares: CompanyShare[] = []) => {
      createdCompanyShares.forEach((companyShare) => {
        companyAssetsStore.addCompanyShare(companyShare);
      });
      onSuccess?.('created');
    };
    const handleAccountUpdated = (
      createdCompanyShares: CompanyShare[] = [],
      deletedCompanyShareIds: string[] = [],
    ) => {
      createdCompanyShares.forEach((companyShare) => {
        companyAssetsStore.addCompanyShare(companyShare);
      });
      deletedCompanyShareIds.forEach((companyShareId) => {
        companyAssetsStore.deleteCompanyShare(companyShareId);
      });
      onSuccess?.('updated');
    };

    const onSubmitAccountForm = () => {
      accountActionsRef.current?.submit(onSubmitAccountFormCallBack);
    };
    const onSubmitAccountFormCallBack = async (data: Account | any) => {
      setIsLoading(true);
      try {
        if (selectedAccountId) {
          try {
            const driversPresentOnModalOpen = drivers;
            const driversPresentOnSubmit = data.drivers;

            const equipmentPresentOnModalOpen = equipment;
            const equipmentPresentOnSubmit = data.equipment;

            const updatedAccount = await updateAccount({
              account: data,
            });

            const driversToCreate = driversPresentOnSubmit?.filter(
              (submittedDrivers: { id: string }) =>
                !driversPresentOnModalOpen.some(
                  (existingDrivers) => existingDrivers.id === submittedDrivers.id,
                ),
            );

            const driversToDelete = driversPresentOnModalOpen.filter(
              (existingDriver) =>
                driversPresentOnSubmit &&
                !driversPresentOnSubmit.some(
                  (submittedDriver: { id: string }) =>
                    existingDriver.id === submittedDriver.id,
                ),
            );

            // Get the company shares for the drivers that we need to remove
            const driverCompanySharesToDelete = sharedDriversCompanyShares.filter(
              (companyShare) =>
                driversToDelete.some(
                  (driver) => driver.id === companyShare.companyShareable.id,
                ),
            );

            const equipmentToCreate = equipmentPresentOnSubmit?.filter(
              (submittedEquipment: { id: string }) =>
                !equipmentPresentOnModalOpen.some(
                  (existingEquipment) => existingEquipment.id === submittedEquipment.id,
                ),
            );

            const equipmentToDelete = equipmentPresentOnModalOpen.filter(
              (existingEquipment) =>
                equipmentPresentOnSubmit &&
                !equipmentPresentOnSubmit.some(
                  (submittedEquipment: { id: string }) =>
                    existingEquipment.id === submittedEquipment.id,
                ),
            );

            // Get the company shares for the equipment that we need to remove
            const equipmentCompanySharesToDelete = sharedEquipmentCompanyShares.filter(
              (companyShare) =>
                equipmentToDelete.some(
                  (equip) => equip.id === companyShare.companyShareable.id,
                ),
            );

            const companySharePromises: Promise<void>[] = [];
            if (updatedAccount) {
              if (
                driversToCreate?.length &&
                updatedAccount?.company?.id &&
                updatedAccount?.connectedCompany?.id
              ) {
                driversToCreate.forEach((driver: DriverBasic) => {
                  companySharePromises.push(
                    createCompanyShareByCompanyId({
                      companyId: updatedAccount.company?.id || '',
                      receiver_company_id: updatedAccount.connectedCompany?.id || '',
                      company_shareable_id: driver.id,
                      company_shareable_type: 'User' as CompanyShareableType,
                    }),
                  );
                });
              }

              if (driverCompanySharesToDelete?.length) {
                driverCompanySharesToDelete.forEach((driverCompanyShare) => {
                  companySharePromises.push(
                    deleteCompanyShare(driverCompanyShare.id as string),
                  );
                });
              }

              if (
                equipmentToCreate?.length &&
                updatedAccount?.company?.id &&
                updatedAccount?.connectedCompany?.id
              ) {
                equipmentToCreate.forEach((equip: BasicEquipment) => {
                  companySharePromises.push(
                    createCompanyShareByCompanyId({
                      companyId: updatedAccount.company?.id || '',
                      receiver_company_id: updatedAccount.connectedCompany?.id || '',
                      company_shareable_id: equip.id,
                      company_shareable_type: 'Equipment' as CompanyShareableType,
                    }),
                  );
                });
              }

              if (equipmentCompanySharesToDelete?.length) {
                equipmentCompanySharesToDelete.forEach((equipCompanyShare) => {
                  companySharePromises.push(
                    deleteCompanyShare(equipCompanyShare.id as string),
                  );
                });
              }
            }

            await Promise.all(companySharePromises);
            handleSaveSuccess(
              t('account.account_updated', { name: data?.name }),
              'updated',
            );
          } catch (error) {
            const axiosError = error as AxiosError<ModelError_Response>;
            setAsyncErrors(axiosError.response?.data.error.errors || []);
          }
        } else if (data?.connected?.value) {
          try {
            const isCreatingAccountForCurrentCompany = data?.company?.id === companyId;
            const newConnectedAccount =
              userStore.isCurrentCompanyActive && isCreatingAccountForCurrentCompany
                ? await createConnectedAccount({
                    account: data,
                  })
                : await createConnectedAccountByCompanyId({ account: data });

            const drivers = data.drivers;
            const equipment = data.equipment;

            const companySharePromises: Promise<void>[] = [];

            if (newConnectedAccount) {
              if (
                drivers?.length &&
                newConnectedAccount?.company?.id &&
                newConnectedAccount?.connectedCompany?.id
              ) {
                drivers.forEach((driver: DriverBasic) => {
                  companySharePromises.push(
                    createCompanyShareByCompanyId({
                      companyId: newConnectedAccount.company?.id || '',
                      receiver_company_id: newConnectedAccount.connectedCompany?.id || '',
                      company_shareable_id: driver.id,
                      company_shareable_type: 'User' as CompanyShareableType,
                    }),
                  );
                });
              }

              if (
                equipment?.length &&
                newConnectedAccount?.company?.id &&
                newConnectedAccount?.connectedCompany?.id
              ) {
                equipment.forEach((equip: BasicEquipment) => {
                  companySharePromises.push(
                    createCompanyShareByCompanyId({
                      companyId: newConnectedAccount.company?.id || '',
                      receiver_company_id: newConnectedAccount.connectedCompany?.id || '',
                      company_shareable_id: equip.id,
                      company_shareable_type: 'Equipment' as CompanyShareableType,
                    }),
                  );
                });
              }
            }

            await Promise.all(companySharePromises);
            handleSaveSuccess(
              t('account.connected_account_created', { name: data?.name }),
              'created',
            );
          } catch (error) {
            const axiosError = error as AxiosError<ModelError_Response>;
            setAsyncErrors(axiosError.response?.data.error.errors || []);
          }
        } else {
          try {
            const account = await createAccount({ account: data });

            handleSaveSuccess(
              t('account.account_created', { name: account.name }),
              'created',
            );
          } catch (error) {
            const axiosError = error as AxiosError<ModelError_Response>;
            setAsyncErrors(axiosError.response?.data.error.errors || []);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (source === 'customers-page') {
      return (
        <CustomerAccountForm
          key={defaultAccount?.id}
          account={defaultAccount}
          isOpen={open || false}
          onAccountCreated={handleAccountCreated}
          onAccountUpdated={handleAccountUpdated}
          onClose={handleClose}
          onSavingChange={setIsFormSaving}
        />
      );
    }

    if (source === 'vendors-page') {
      return (
        <VendorAccountFormDialogWrapper
          key={defaultAccount?.id}
          account={defaultAccount}
          isOpen={open || false}
          onAccountCreated={handleAccountCreated}
          onAccountUpdated={handleAccountUpdated}
          onClose={handleClose}
          onSavingChange={setIsFormSaving}
        />
      );
    }

    return (
      <>
        <Dialog
          isOpen={open || false}
          maxWidth="lg"
          onClose={handleClose}
          preventCloseOnBackdropClick
        >
          <DialogHeader onClose={handleClose}>
            {selectedAccountId
              ? t('account.update_account')
              : t('account.create_account')}
          </DialogHeader>

          <DialogContent sx={(theme) => ({ bgcolor: theme.semantic.surfaceSunken })}>
            <AccountForm
              ref={accountActionsRef}
              defaultAccount={defaultAccount ?? null}
              errors={asyncErrors}
              onFormStateChange={onFormStateChange}
              sharedDrivers={drivers}
              sharedDriversCompanyShares={sharedDriversCompanyShares}
              sharedEquipment={equipment}
              sharedEquipmentCompanyShares={sharedEquipmentCompanyShares}
            />
          </DialogContent>

          <DialogActions>
            <SmallButton
              color="secondary"
              disabled={isLoading}
              onClick={handleClose}
              variant="outlined"
            >
              {t('actions.cancel')}
            </SmallButton>

            <SmallLoadingButton
              color="primary"
              data-test-id="create-account-btn"
              disabled={isLoading || !currentFormDirty}
              loading={isLoading}
              onClick={onSubmitAccountForm}
            >
              {selectedAccountId ? t('actions.submit') : t('actions.create')}
            </SmallLoadingButton>
          </DialogActions>
        </Dialog>
      </>
    );
  },
);
export { AccountsDialog };
